/**
 * Every limit a device is under, "sent" beside "agent confirms" (release 3.2
 * telemetry item 5). The comparison is the controller's (limits-comparison.ts);
 * this card only says it plainly. "Sent" names who asked - the console, the
 * fleet policy, the install, or the agent's own default - because a customer
 * reading "5%" needs to know whether that is their setting or ours.
 */

import { ShieldCheck } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import type { EdgeLimitRow } from '@/lib/api';

import { NOT_REPORTED } from './edge-measurement-format';

export function sentByText(by: string): string {
  switch (by) {
    case 'console':
      return 'console';
    case 'fleet_policy':
      return 'fleet policy';
    case 'install':
      return 'install settings';
    case 'agent_default':
      return 'agent default';
    case 'agent_reported':
      return 'as the agent reports it';
    default:
      return by.replace(/_/g, ' ');
  }
}

const STATE: Record<string, { label: string; variant: 'success' | 'warning' | 'critical' | 'glass' | 'info' }> = {
  confirmed: { label: 'confirmed', variant: 'success' },
  differs: { label: 'differs', variant: 'critical' },
  pending: { label: 'not yet applied', variant: 'warning' },
  not_reported: { label: 'not reported', variant: 'glass' },
  not_sent: { label: 'not set', variant: 'glass' },
  // Release 3.6 #27: held by the agent, but its latest scan had no target the
  // limit applies to, so it holds nothing back.
  not_matched: { label: 'not matched', variant: 'warning' },
  // PR #191 round 3: sent to an agent that reports in but is too old to apply
  // it. A warning: the operator set a cap that nothing enforces.
  not_enforced: { label: 'not enforced', variant: 'warning' },
};

export function stateLabel(state: string): { label: string; variant: 'success' | 'warning' | 'critical' | 'glass' | 'info' } {
  return STATE[state] ?? { label: state.replace(/_/g, ' '), variant: 'glass' };
}

/** "11 of 13 confirmed; 1 differs; 1 not reported." */
export function limitsSummary(rows: EdgeLimitRow[]): string {
  const n = (s: string) => rows.filter((r) => r.state === s).length;
  const parts = [`${n('confirmed')} of ${rows.length} confirmed by the agent`];
  if (n('differs') > 0) parts.push(`${n('differs')} differ${n('differs') === 1 ? 's' : ''}`);
  if (n('pending') > 0) parts.push(`${n('pending')} not yet applied`);
  if (n('not_reported') > 0) parts.push(`${n('not_reported')} not reported`);
  if (n('not_matched') > 0) parts.push(`${n('not_matched')} not matched by any target`);
  if (n('not_enforced') > 0) parts.push(`${n('not_enforced')} not enforced by this agent version`);
  return `${parts.join('; ')}.`;
}

export function LimitsCard({ rows }: { rows: EdgeLimitRow[] | undefined }) {
  if (!rows || rows.length === 0) return null;
  return (
    <GlassCard className="p-3" data-testid="limits-card">
      <CardHeader className="p-0 mb-2">
        <CardTitle className="text-sm inline-flex items-center gap-1.5">
          <ShieldCheck className="h-4 w-4" /> Limits: sent and confirmed by the agent
        </CardTitle>
        <CardDescription className="text-[11px]" data-testid="limits-summary">
          {limitsSummary(rows)} &quot;Confirmed&quot; is the agent&apos;s own report of what is in force; for the CPU
          ceiling and the priority, what the operating system granted it.
        </CardDescription>
      </CardHeader>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="py-1 pr-2 font-medium">Limit</th>
              <th className="py-1 pr-2 font-medium">Sent</th>
              <th className="py-1 pr-2 font-medium">Agent confirms</th>
              <th className="py-1 font-medium">State</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const s = stateLabel(r.state);
              return (
                <tr key={r.key} className="border-t border-border/50 align-top" data-testid={`limit-${r.key}`}>
                  <td className="py-1.5 pr-2 font-medium">{r.label}</td>
                  <td className="py-1.5 pr-2" data-testid={`limit-${r.key}-sent`}>
                    {r.sent ? (
                      <>
                        <span className="break-all">{r.sent.value}</span>
                        <div className="text-[11px] text-muted-foreground">{sentByText(r.sent.by)}</div>
                      </>
                    ) : (
                      <span className="text-muted-foreground">not set</span>
                    )}
                  </td>
                  <td className="py-1.5 pr-2" data-testid={`limit-${r.key}-confirmed`}>
                    {r.confirmed ? (
                      <>
                        <span className="break-all">{r.confirmed.value}</span>
                        {r.confirmed.detail && (
                          <div className="text-[11px] text-muted-foreground">{r.confirmed.detail}</div>
                        )}
                      </>
                    ) : (
                      <span className="text-muted-foreground">{NOT_REPORTED} not reported</span>
                    )}
                    {r.note && <div className="text-[11px] text-foreground/90">{r.note}</div>}
                  </td>
                  <td className="py-1.5">
                    <Badge variant={s.variant} className="text-[10px]" data-testid={`limit-${r.key}-state`}>
                      {s.label}
                    </Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </GlassCard>
  );
}
